<template>
  <CollapsibleSection
    v-if="getValue('client_type') === 'professionnel'"
    title="Informations professionnelles"
    icon="fas fa-building"
    context="quote"
    storage-key="professionnel-info"
    :bare="bare"
  >
    <DetailItem label="Nom de l'établissement" :copyable="false">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_nom_etablissement"
        class="form-control"
      />
      <router-link
        v-else-if="quote.professionnel_nom_etablissement"
        :to="contactDetailPathOf(quote)"
        class="contact-link"
      >
        {{ quote.professionnel_nom_etablissement }}
      </router-link>
      <DetailEmpty v-else />
    </DetailItem>
    <!-- La copie au clic prend le SIRET seul, pas le libellé du lien Pappers posé à côté. -->
    <DetailItem label="SIRET" :copy-value="quote.professionnel_siret || ''">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_siret"
        pattern="[0-9]{14}"
        minlength="14"
        maxlength="14"
        @input="handleSiretInput"
        class="form-control"
      />
      <template v-else>
        <template v-if="!isMissing(quote.professionnel_siret)">
          <span>{{ quote.professionnel_siret }}</span>
          <PappersLink :siret="quote.professionnel_siret" />
        </template>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem v-if="getValue('professionnel_etablissement_id')" label="ID Établissement">
      {{ getValue('professionnel_etablissement_id') }}
    </DetailItem>
    <DetailItem
      v-if="getValue('professionnel_activite_principale') || getValue('company_sante')"
      label="Santé entreprise"
      :copyable="false"
    >
      <CompanySanteBadges
        :naf-code="getValue('professionnel_activite_principale')"
        :company-sante="getValue('company_sante')"
        :alertes="getValue('company_alertes')"
        :siret="getValue('professionnel_siret')"
      >
        <button
          v-if="!isEditing && getValue('professionnel_siret')"
          type="button"
          class="row-action refresh-btn"
          :disabled="isRefreshing"
          @click="handleRefresh"
        >
          {{ isRefreshing ? 'Actualisation…' : 'Rafraîchir les données INSEE' }}
        </button>
      </CompanySanteBadges>
    </DetailItem>
    <DetailItem
      v-else-if="!isEditing && getValue('professionnel_siret')"
      label="Santé entreprise"
      :copyable="false"
    >
      <CompanySanteBadges show-unknown>
        <button
          type="button"
          class="row-action refresh-btn"
          :disabled="isRefreshing"
          @click="handleRefresh"
        >
          {{ isRefreshing ? 'Actualisation…' : 'Récupérer les données INSEE' }}
        </button>
      </CompanySanteBadges>
    </DetailItem>
    <DetailItem label="Adresse">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_adresse"
        class="form-control"
      />
      <template v-else>
        <span v-if="!isMissing(quote.professionnel_adresse)">{{ quote.professionnel_adresse }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="Code postal">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_code_postal"
        pattern="[0-9]{5}"
        minlength="5"
        maxlength="5"
        @input="handleCodePostalInput"
        class="form-control"
      />
      <template v-else>
        <span v-if="!isMissing(quote.professionnel_code_postal)">{{ quote.professionnel_code_postal }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="Ville">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_ville"
        class="form-control"
      />
      <template v-else>
        <span v-if="!isMissing(quote.professionnel_ville)">{{ quote.professionnel_ville }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="TVA">
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.professionnel_tva"
        :options="tvaOptions"
      />
      <template v-else>
        <span v-if="!isMissing(quote.professionnel_tva)">{{ quote.professionnel_tva }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem v-if="getValue('professionnel_tva') === 'Oui'" label="Numéro de TVA">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.professionnel_numero_tva"
        class="form-control"
        placeholder="FR12345678901"
      />
      <template v-else>
        <span v-if="!isMissing(quote.professionnel_numero_tva)">{{ quote.professionnel_numero_tva }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="Statut de l'établissement" :copyable="false">
      <Badge v-if="quote.professionnel_est_public === true" variant="info" size="sm">
        Public / Administration
      </Badge>
      <Badge v-else-if="quote.professionnel_est_public === false" variant="secondary" size="sm">
        Privé
      </Badge>
      <DetailEmpty v-else />
    </DetailItem>
    <!-- Ce que seul le tunnel d'analyse du besoin ajoute : la question sur
         l'activité de l'établissement, juste sous son statut. -->
    <slot name="besoin-extra" />
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { ref } from 'vue'
import { CollapsibleSection, DetailItem, DetailEmpty } from '../data'
import { RadioGroup, Badge } from '../ui'

const tvaOptions = [
  { value: 'Oui', label: 'Oui' },
  { value: 'Non', label: 'Non' },
  { value: 'Ne sais pas', label: 'Ne sais pas' },
]
import { useFormFormatting } from '../../composables/useFormFormatting.js'
import { api, endpoints } from '../../services/api.js'
import { toast } from 'vue-sonner'
import { RESULTAT_ENRICHISSEMENT } from '@shared/sante-entreprise.js'
import CompanySanteBadges from '../shared/CompanySanteBadges.vue'
import PappersLink from '../shared/PappersLink.vue'
import { contactDetailPathOf } from '../../utils/contactRoute.js'

const props = defineProps({
  /** Rend la section sans carte ni en-tête, pour l'imbriquer dans un bloc regroupé. */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  },
  getValue: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['refresh'])

const { formatSiret, formatCodePostal } = useFormFormatting()

const isRefreshing = ref(false)

const isMissing = (value) => {
  return !value || value === '' || value === null || value === undefined
}

const handleRefresh = async () => {
  isRefreshing.value = true
  try {
    const reponse = await api.patch(endpoints.quotes.refreshCompanyData(props.quote.id))
    // Un SIRET que l'INSEE ne connaît pas n'est pas une réussite : le badge passe à « SIRET inexistant ».
    if (reponse?.resultat === RESULTAT_ENRICHISSEMENT.INTROUVABLE) toast.warning(reponse.message)
    else toast.success('Données INSEE mises à jour')
    emit('refresh')
  } catch (error) {
    // 503 : l'INSEE ou le BODACC n'a pas répondu, et rien n'a été modifié — le serveur le dit.
    toast.error(error.status === 503 ? error.message : 'Impossible de récupérer les données INSEE')
  } finally {
    isRefreshing.value = false
  }
}

const handleSiretInput = (event) => {
  const formatted = formatSiret(event.target.value)
  props.editedQuote.professionnel_siret = formatted
}

const handleCodePostalInput = (event) => {
  const formatted = formatCodePostal(event.target.value)
  props.editedQuote.professionnel_code_postal = formatted
}
</script>

<style scoped>
.contact-link {
  color: var(--color-primary);
  text-decoration: underline;
  transition: color var(--transition-base);
  cursor: pointer;
}

.contact-link:hover {
  color: var(--color-primary-dark);
}

.form-control {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-dark);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  font-family: inherit;
}

.form-control:focus {
  outline: 2px solid var(--color-info);
  outline-offset: 2px;
  border-color: var(--color-info);
  box-shadow: 0 0 0 3px var(--color-info-lighter);
}

/* Action de ligne (cf. .row-action) : le libellé reste lisible, l'action ne
   se devine pas derrière une icône seule. */
.refresh-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
